import { useEffect, useState } from 'react';

import type { PaginationState } from '@tanstack/react-table';
import { useSearchParams } from 'react-router';

import { useFromPageState } from '@/hooks/useFromPageState';
import { TABLE_PAGINATION } from '@/util/constants';

/**
 * Synchronize TanStack Table pagination state with the URL's `page` query parameter.
 *
 * @param defaultPageSize - Optional page size to initialize the table pagination with.
 * @returns Pagination state and dispatcher wired to the URL search params.
 */
export function useUrlPagination(defaultPageSize = TABLE_PAGINATION) {
  // Capture current query params and keep pagination tied to the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromState = useFromPageState();

  const pageParam = searchParams.get('page');
  const parsedPage = pageParam !== null ? Number.parseInt(pageParam, 10) : Number.NaN;
  const pageFromUrl = Number.isNaN(parsedPage) ? undefined : Math.max(parsedPage - 1, 0);
  const initialPage = pageFromUrl ?? pageFromState ?? 0;

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPage,
    pageSize: defaultPageSize,
  });

  useEffect(() => {
    // Replace (not push) the page index in the query string to avoid polluting browser history.
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', String(pagination.pageIndex + 1));
    setSearchParams(params, { replace: true });
  }, [pagination.pageIndex, searchParams, setSearchParams]);

  return { pagination, setPagination };
}
